// src/store/slices/categorySlice.ts
import { createSlice, createAsyncThunk, type PayloadAction } from '@reduxjs/toolkit'

import apiClient from '@/libs/apiClient'
import type { Category, Subcategory } from '@/types/catalog'

export interface ApiCategory {
  id: string
  numeric_id?: number
  title: string
  title_en: string
  title_ar: string
  description_en: string
  description_ar: string
  products_count: number
  subcategories_count?: number
  is_enabled: boolean
  order?: number
  image?: {
    url: string
    type: string
    document_type: string
    disk: string
    created_at: string
  }
  created_at: string
  updated_at: string
}

export const mapApiToCategory = (api: ApiCategory): Category => ({
  id: api.id,
  numeric_id: api.numeric_id || 0,
  name: api.title_en,
  nameAr: api.title_ar,

  // title_en can come back as a bare number if someone typed digits-only into
  // the title field (JSON encodes an all-numeric string as a number) —
  // String() first so this never throws and silently breaks the fetch.
  slug: String(api.title_en ?? '').toLowerCase().replace(/ /g, '-'),
  icon: 'tabler-category',
  image: api.image?.url || undefined,
  description: api.description_en,
  descriptionAr: api.description_ar,
  subcategories: [],
  order: api.order ?? 0,
  isActive: Boolean(api.is_enabled),
  isArchived: false,
  productsCount: api.products_count,
  subcategoriesCount: api.subcategories_count || 0,
  createdAt: api.created_at,
  updatedAt: api.updated_at
})

interface Pagination {
  currentPage: number
  lastPage: number
  perPage: number
  total: number
}

interface CategoryState {
  categories: Category[]
  pagination: Pagination | null
  loading: boolean
  error: string | null
}

const initialState: CategoryState = {
  categories: [],
  pagination: null,
  loading: false,
  error: null
}

// ============================
// Add Category
// ============================
export const addCategory = createAsyncThunk<ApiCategory, FormData, { rejectValue: string }>(
  'categories/addCategory',
  async (formData, { rejectWithValue }) => {
    try {
      const res = await apiClient.post('/admin/v1/categories/add', formData)
      const data = res.data

      return data.data as ApiCategory
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || err.message || 'Failed to add category')
    }
  }
)

// ============================
// Edit Category
// ============================
export const editCategory = createAsyncThunk<ApiCategory, FormData, { rejectValue: string }>(
  'categories/editCategory',
  async (formData, { rejectWithValue }) => {
    try {
      const id = formData.get('id')
      const res = await apiClient.post(`/admin/v1/categories/${id}/edit`, formData)
      const data = res.data

      return data.data as ApiCategory
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || err.message || 'Failed to edit category')
    }
  }
)

export const deleteCategory = createAsyncThunk<string, string, { rejectValue: string }>(
  'categories/deleteCategory',
  async (id: string, { rejectWithValue }) => {
    try {
      await apiClient.delete(`/admin/v1/categories/${id}/delete`)

      return id
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || err.message || 'Failed to delete category')
    }
  }
)

// ============================
// Toggle Category Status
// ============================
export const toggleCategory = createAsyncThunk<
  ApiCategory,
  { id: string; is_enabled: boolean },
  { rejectValue: string }
>('categories/toggleCategory', async ({ id, is_enabled }, { rejectWithValue }) => {
  try {
    const res = await apiClient.post(`/admin/v1/categories/${id}/toggle`, { is_enabled })

    return res.data.data as ApiCategory
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message || err.message || 'Failed to toggle category status')
  }
})

// ============================
// Fetch Categories
// ============================
export const fetchCategories = createAsyncThunk<
  { categories: Category[]; pagination: Pagination },
  { limit?: number; lang?: 'ar' | 'en' } | undefined,
  { rejectValue: string }
>('categories/fetchCategories', async (params = {}, { rejectWithValue }) => {
  try {
    const limit = params.limit || 1000
    const lang = params.lang || 'en'
    const res = await apiClient.get('/admin/v1/categories', { params: { limit, lang } })
    const json = res.data

    // Transform API response structure (ApiCategory) to the Category structure expected by the frontend
    const categories: Category[] = json.data.map(
      (cat: ApiCategory): Category => ({
        id: cat.id,
        numeric_id: cat.numeric_id || 0,
        name: cat.title_en,
        nameAr: cat.title_ar,
        slug: String(cat.title_en ?? '').toLowerCase().replace(/ /g, '-'),
        icon: 'tabler-category',
        image: cat.image?.url,
        description: cat.description_en,
        descriptionAr: cat.description_ar,
        subcategories: [] as Subcategory[],
        order: cat.order ?? 0,
        isActive: Boolean(cat.is_enabled),
        isArchived: false,
        productsCount: cat.products_count,
        subcategoriesCount: cat.subcategories_count || 0,
        createdAt: cat.created_at,
        updatedAt: cat.updated_at
      })
    )

    const pagination: Pagination = {
      currentPage: json.meta.currentPage,
      lastPage: json.meta.lastPage,
      perPage: json.meta.perPage,
      total: json.meta.total
    }

    return { categories, pagination }
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message || err.message || 'Failed to fetch categories')
  }
})

// ============================
// Reorder Categories
// ============================
// The dashboard sends the full list of ids in the order the admin dragged them
// into; the API replies with the reordered list.
export const reorderCategories = createAsyncThunk<Category[], string[], { rejectValue: string }>(
  'categories/reorderCategories',
  async (ids, { rejectWithValue }) => {
    try {
      const res = await apiClient.post('/admin/v1/categories/reorder', { categories: ids })

      return (res.data.data as ApiCategory[]).map(mapApiToCategory)
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || err.message || 'Failed to reorder categories')
    }
  }
)

// Slice
const categorySlice = createSlice({
  name: 'categories',
  initialState,
  reducers: {
    clearCategories(state) {
      state.categories = []
      state.pagination = null
      state.error = null
    },

    // Apply a drag straight away so the list does not jump while the request
    // is in flight; reorderCategories.fulfilled then replaces it with the
    // server's version.
    setCategoryOrder(state, action: PayloadAction<string[]>) {
      const byId = new Map(state.categories.map(c => [c.id, c]))

      state.categories = action.payload
        .map(id => byId.get(id))
        .filter((c): c is Category => Boolean(c))
        .map((c, index) => ({ ...c, order: index + 1 }))
    }
  },
  extraReducers: builder => {
    builder
      .addCase(fetchCategories.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(
        fetchCategories.fulfilled,
        (state, action: PayloadAction<{ categories: Category[]; pagination: Pagination }>) => {
          state.loading = false
          state.categories = action.payload.categories
          state.pagination = action.payload.pagination
        }
      )
      .addCase(fetchCategories.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload || 'Failed to fetch categories'
      })
      .addCase(addCategory.pending, state => {
        state.error = null
      })

      .addCase(addCategory.fulfilled, (state, action: PayloadAction<ApiCategory>) => {
        state.loading = false
        state.categories.push(mapApiToCategory(action.payload))

        // Update total count if pagination exists
        if (state.pagination) {
          state.pagination.total += 1
        }
      })

      .addCase(addCategory.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload || 'Failed to edit category'
      })

      .addCase(editCategory.pending, state => {
        state.error = null
      })
      .addCase(editCategory.fulfilled, (state, action: PayloadAction<ApiCategory>) => {
        state.loading = false

        const updated = mapApiToCategory(action.payload)

        state.categories = state.categories.map(c => (c.id === updated.id ? updated : c))
      })
      .addCase(editCategory.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload || 'Failed to add category'
      })

      .addCase(reorderCategories.fulfilled, (state, action: PayloadAction<Category[]>) => {
        state.categories = action.payload
        state.error = null
      })
      .addCase(reorderCategories.rejected, (state, action) => {
        state.error = action.payload || 'Failed to reorder categories'
      })
      .addCase(deleteCategory.fulfilled, (state, action: PayloadAction<string>) => {
        state.loading = false

        state.categories = state.categories.filter(c => c.id !== action.payload)
      })

      // Toggle - optimistic update on pending, no loading/shimmer
      .addCase(toggleCategory.pending, (state, action) => {
        const { id, is_enabled } = action.meta.arg

        state.categories = state.categories.map(c =>
          c.id === id ? { ...c, isActive: is_enabled } : c
        )
      })
      .addCase(toggleCategory.fulfilled, (state, action: PayloadAction<ApiCategory>) => {
        const updated = mapApiToCategory(action.payload)

        state.categories = state.categories.map(c => (c.id === updated.id ? updated : c))
      })
      .addCase(toggleCategory.rejected, (state, action) => {
        // Revert optimistic update
        const { id, is_enabled } = action.meta.arg

        state.categories = state.categories.map(c =>
          c.id === id ? { ...c, isActive: !is_enabled } : c
        )
      })
  }
})

export const { clearCategories, setCategoryOrder } = categorySlice.actions
export default categorySlice.reducer
